/**
 * # PDPProductOverviewGallery
 * Custom HTML element for displaying a product gallery
 * with thumbnails and a main image placeholder.
 */

class PDPProductOverviewGallery extends HTMLElement {
  constructor() {
    super();
    this.SCROLL_BY_PIXELS = 95;
    Shopify.ProductGalleryFilterByVariantHandle =
      this.filterGalleryByVariantHandle.bind(this);
    this.galleryThumbnailElementClass = ".gallery-thumbnail";
    this.breakpoint = window.matchMedia(`(max-width: 1023px)`);
    this.mobileSlider = null;
  }

  /**
   * Lifecycle method called when the element is inserted into the DOM.
   * Configures event listeners and initializes properties.
   */
  connectedCallback() {
    this.galleryThumbnailSlider = this.querySelector(
      "#pdp-product-overview-gallery-thumbnail-and-mobile-slider"
    );

    this.galleryThumbnailList = this.querySelector(
      "#pdp-product-overview-gallery-thumbnail-and-mobile-slider .gallery-thumbnail-list"
    );

    this.scrollerBtn = this.querySelector(
      "#pdp-product-overview-gallery-thumbnail-scroller"
    );

    this.mainImageContainer = this.querySelector(
      "#pdp-product-overview-gallery-main-image"
    );

    this.mainImagePlaceholder = this.querySelector(
      "#pdp-product-overview-gallery-main-image-placeholder"
    );

    this.gallerySwiper = this.querySelector(
      "#pdp-product-overview-gallery-thumbnail-and-mobile-slider .swiper"
    );

    this.galleryPrevBtn = this.querySelector(
      "#pdp-product-overview-gallery-thumbnail-and-mobile-slider .swiper-button-prev"
    );

    this.galleryNextBtn = this.querySelector(
      "#pdp-product-overview-gallery-thumbnail-and-mobile-slider .swiper-button-next"
    );

    this.galleryThumbnailSlider.addEventListener(
      "click",
      this.galleryEventsDispatcher.bind(this)
    );

    this.loadSection();
  }

  /**
   * Lifecycle method called when the element is removed from the DOM.
   * Removes event listeners and performs cleanup.
   */
  disconnectedCallback() {
    this.galleryThumbnailSlider.removeEventListener(
      "click",
      this.galleryThumbnailSlider.bind(this),
      false
    );
  }

  /**
   * Functions/Actions when the section loads
   */
  loadSection() {
    // Toggle gallery scroller button
    const featuredMedia =
      this.galleryThumbnailList.querySelectorAll(".gallery-thumbnail").length;
    if (featuredMedia > 5) this.showGalleryScroller(true);
    else this.showGalleryScroller(false);

    // Toggle mobile slider arrows
    if (featuredMedia > 1) this.showMobileSliderArrows(true);
    else this.showMobileSliderArrows(false);

    // Build mobile slider
    if (featuredMedia > 1) this.initMobileSlider();
  }

  /**
   * Event handler for gallery events
   * @param {Event} event - The event object.
   */
  galleryEventsDispatcher(event) {
    const thumbnail = event.target.closest(
      `${this.galleryThumbnailElementClass}`
    );

    const scrollerBtn = event.target.closest(
      "#pdp-product-overview-gallery-thumbnail-scroller"
    );

    if (thumbnail) {
      this.clickedThumbnailController(thumbnail);
      return;
    }

    if (scrollerBtn) {
      this.clickedScrollerBtnController();
      return;
    }
  }

  /**
   * Handles the click event on a gallery thumbnail.
   * @param {HTMLElement} thumbnail - The clicked thumbnail element.
   */
  clickedThumbnailController(thumbnail) {
    const { mainImageUrl, mediaType, externalMediaId } = thumbnail.dataset;

    this.removeAllActiveThumbnailState();
    this.setActiveThumbnail(thumbnail, true);
    this.removeAllActiveGalleryIframeState();

    if (mediaType === "image") {
      this.setMainImageSource(mainImageUrl);
      this.setGalleryImagePlaceholder(true);
    }

    if (mediaType === "video") {
      this.setGalleryImagePlaceholder(false);
      const element = this.mainImageContainer.querySelector(
        `[data-media-id="${externalMediaId}"]`
      );
      this.setActiveGalleryIframe(element, true);
    }

    if (mediaType === "external_video") {
      this.setActiveGalleryIframe(
        this.mainImageContainer.querySelector(
          `[data-media-id="${externalMediaId}"]`
        ),
        true
      );
      this.setGalleryImagePlaceholder(false);
    }
  }

  /**
   * Handles the click event on the scroller button.
   * It scrolls the galleryThumbnailList by a specified number of pixels in a smooth manner.
   */
  clickedScrollerBtnController() {
    this.galleryThumbnailList.scrollBy({
      behavior: "smooth",
      top: this.SCROLL_BY_PIXELS,
    });
  }

  /**
   * Toggles the active state of a thumbnail.
   * @param {HTMLElement} thumbnail - The thumbnail element.
   * @param {boolean} flag - The flag indicating whether to set or unset the active state.
   */
  setActiveThumbnail(thumbnail, flag) {
    thumbnail.dataset.isActive = flag;
  }

  /**
   * Updates the source of the main image placeholder.
   * @param {string} src - The source URL of the main image.
   */
  setMainImageSource(src) {
    this.mainImagePlaceholder.src = src;
  }

  /**
   * Toggles the active state of an iframe
   * @param {HTMLElement} element iframe
   * @param {boolean} flag The flag indication to set or unset active state
   */
  setActiveGalleryIframe(element, flag) {
    element.dataset.showVideoMedia = flag;
  }

  /**
   * Toggles the active state of the main image
   * @param {boolean} flag The flag indication to set or unset active state
   */
  setGalleryImagePlaceholder(flag) {
    this.mainImagePlaceholder.dataset.showPlaceholderImage = flag;
  }

  showGalleryScroller(flag) {
    this.scrollerBtn.dataset.showGalleryThumbnailScroller = flag;
  }

  showMobileSliderArrows(flag) {
    this.galleryNextBtn.dataset.showGalleryMobileSliderArrow = flag;
    this.galleryPrevBtn.dataset.showGalleryMobileSliderArrow = flag;
  }

  /**
   * Removes the active state from all thumbnails within the gallery.
   */
  removeAllActiveThumbnailState() {
    this.galleryThumbnailSlider
      .querySelectorAll(`${this.galleryThumbnailElementClass}`)
      .forEach((el) => (el.dataset.isActive = false));
  }

  /**
   * Removes the active state from all iframe within the gallery.
   */
  removeAllActiveGalleryIframeState() {
    this.mainImageContainer
      .querySelectorAll(`.gallery-main-video`)
      .forEach((el) => {
        el.dataset.showVideoMedia = false;
        let iframe = el.querySelector("iframe");
        let video = el.querySelector("video");
        if (iframe) {
          iframe.src = iframe.src;
        }
        if (video) {
          video.querySelector("source").src = video.querySelector("source").src;
          video.pause();
        }
      });

    const activeIframe = this.mainImageContainer.querySelector(
      `iframe[data-show-video-media="true"]`
    );
    if (!activeIframe) return;
    activeIframe.src = activeIframe.src;
    activeIframe.dataset.showVideoMedia = false;
  }

  initMobileSlider() {
    if (!this.breakpoint.matches) return;
    import(flu.chunks.swiper).then((_ref) => {
      let { Swiper, Navigation } = _ref;
      this.mobileSlider = new Swiper(this.gallerySwiper, {
        modules: [Navigation],
        slidesPerView: 1.17,
        centeredSlides: true,
        spaceBetween: 4,
        resistance: false,
        navigation: {
          prevEl: this.galleryPrevBtn,
          nextEl: this.galleryNextBtn,
        },
        loop: false,
      });
      this.mobileSlider.slideToLoop(0, 0);
    });
  }

  /**
   * # Public API | Filter gallery by a variant handle
   * Filter the gallery thumbnails by a specified variant handle.
   * @param {String} handle The variant handle used for filtering the gallery.
   */
  filterGalleryByVariantHandle(handle = "") {
    if (!handle) return;

    this.removeAllActiveThumbnailState();

    this.mobileSlider?.destroy(true, true);

    this.galleryThumbnailSlider
      .querySelectorAll(`${this.galleryThumbnailElementClass}`)
      .forEach((thumbnail) => {
        // Retrieve the variant handle associated with the thumbnail.
        const { thumbnailHandle } = thumbnail.dataset;

        // Check if the thumbnail's handle matches the specified handle or is "global".
        const handleIsEqual =
          thumbnailHandle === handle || thumbnailHandle === "global";

        // Set a data attribute to mark whether the thumbnail is a filtered image.
        thumbnail.dataset.filteredImage = handleIsEqual ? true : false;
      });

    // Select all thumbnails that are marked as filtered images.
    const filteredElements = Array.from(
      this.galleryThumbnailSlider.querySelectorAll(
        `${this.galleryThumbnailElementClass}[data-filtered-image="true"]`
      )
    );

    // Select the first thumbnail with media type "image"
    const firstMediaImageThumbnail = filteredElements.find(
      (el) =>
        el.dataset.mediaType === "image" &&
        el.dataset.thumbnailHandle !== "global"
    );

    // Select the last thumbnail with media type "image"
    const lastMediaImageThumbnail = filteredElements
      .filter((el) => el.dataset.mediaType === "image")
      .slice(-1)[0];

    // Get all external video thumbnails
    const externalVideoThumbnails = filteredElements.filter(
      (el) => el.dataset.mediaType === "external_video"
    );

    // Get all global images (always shown)
    const globalImages = filteredElements.filter(
      (el) =>
        el.dataset.thumbnailHandle === "global" &&
        el.dataset.mediaType === "image"
    );

    // Only reorder if there are variant-specific images (not all global)
    // This prevents images from shifting when all images are global
    if (firstMediaImageThumbnail !== undefined) {
      if (globalImages.length > 0) {
        // Get last global image
        const lastGlobalImage = globalImages[0];

        // Insert global images after the last thumbnail with media type "image"
        globalImages.forEach((el) =>
          lastMediaImageThumbnail.insertAdjacentElement("afterend", el)
        );

        // Insert external videos after the last thumbnail with "global" handle
        externalVideoThumbnails.forEach((el) =>
          lastGlobalImage.insertAdjacentElement("afterend", el)
        );
      } else {
        // Insert external videos after the last last media image thumbnail handle
        externalVideoThumbnails.forEach((el) =>
          lastMediaImageThumbnail.insertAdjacentElement("afterend", el)
        );
      }
    }

    if (firstMediaImageThumbnail !== undefined) {
      this.setMainImageSource(firstMediaImageThumbnail.dataset.mainImageUrl);
      this.setActiveThumbnail(firstMediaImageThumbnail, true);
    } else {
      this.setMainImageSource(filteredElements[0].dataset.mainImageUrl);
      this.setActiveThumbnail(filteredElements[0], true);
    }

    this.initMobileSlider();
    return;
  }
}

customElements.define(
  "pdp-product-overview-gallery",
  PDPProductOverviewGallery
);
